import { Form, Formik, FormikProps } from 'formik';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { RecipientDetailDto } from 'src/store/recipients/recipient';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  recipient: Nullable<RecipientDetailDto>;
  onCancel: () => void;
  onSubmit: (recipient: RecipientDetailDto) => void;
};

export const RecipientForm = forwardRef<FormikProps<RecipientDetailDto>, Props>(
  ({ recipient, onSubmit, onCancel }, ref) => {
    const { t } = useTranslation();

    const validate = (recipient: RecipientDetailDto) => {
      const validationRunners = [
        validationRunner('no', t('props.number'), required),
        validationRunner('name', t('props.name'), required, maxLength(50))
      ];
      return runValidations(recipient, validationRunners);
    };

    return (
      <Formik
        initialValues={recipient ?? ({} as RecipientDetailDto)}
        validate={validate}
        innerRef={ref}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          submitCount
        }) => (
          <Form onSubmit={handleSubmit}>
            <InputField
              name="no"
              type="number"
              label={t('props.number')}
              value={values.no}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.no}
              displayError={touched.no || submitCount > 0}
            />
            <InputField
              name="name"
              label={t('props.name')}
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.name}
              displayError={touched.name || submitCount > 0}
            />

            <ModalFooter>
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
              />
              <PrimaryButton
                type="submit"
                label={t('save.verb')}
                disabled={isSubmitting}
              />
            </ModalFooter>
          </Form>
        )}
      </Formik>
    );
  }
);
